import { useState, useMemo } from 'react';
import { searchInColumnsGroups, findSelectedPersonalViewItem, findDefaultPersonalViewItem } from './tablePersonalViewUseCase';

// Utils
import groupOptionsByOptGroup from '../../utils/optGroup/optGroup';

/**
 * @typedef {object} useTablePersonalViewReturn
 * @property {Array<{lib: string, options: Array<ApiManagerPersonalViewsOption>}>} personalViewsListFiltered
 * @property {string} searchPersonalViewsGroups
 * @property {(search: string) => void} handleSearchPersonalViewsGroups
 * @property {ApiManagerPersonalViewsOption | null} selectedPersonalViewItem
 * @property {ApiManagerPersonalViewsOption | null} defaultPersonalViewItem
 * @property {boolean} popoverPersonalViewIsOpen
 * @property {(isOpen: boolean) => void} setPopoverPersonalViewIsOpen
 */

/**
 * Hook to manage the table personal view
 * @param {object} props
 * @param {boolean} props.isLoading
 * @param {ApiManagerPersonalViews} [props.personalViews]
 * @returns {useTablePersonalViewReturn} The table personal view state and actions
 */
const useTablePersonalView = ({
  isLoading,
  personalViews,
}) => {
  // State
  const [searchPersonalViewsGroups, setSearchPersonalViewsGroups] = useState('');
  const [popoverPersonalViewIsOpen, setPopoverPersonalViewIsOpen] = useState(false);

  /**
   * Handle searching personal views groups
   * @param {string} search - The search
   */
  const handleSearchPersonalViewsGroups = (search) => {
    setSearchPersonalViewsGroups(search);
  };

  /**
   * @description The personal views filtered
   * @type {Array<{lib: string, options: Array<ApiManagerPersonalViewsOption>}>}
   */
  const personalViewsListFiltered = useMemo(
    () => {
      if ((!personalViews && !personalViews?.options?.length) || isLoading) return [];
      const filteredPersonalViews = searchInColumnsGroups(personalViews.options, searchPersonalViewsGroups);
      return /** @type {Array<{lib: string, options: Array<ApiManagerPersonalViewsOption>}>} */ (
        groupOptionsByOptGroup(filteredPersonalViews)
      );
    },
    [personalViews, searchPersonalViewsGroups, isLoading],
  );

  const selectedPersonalViewItem = useMemo(
    () => {
      if ((!personalViews && !personalViews?.options?.length) || isLoading) return null;
      return findSelectedPersonalViewItem(personalViews.options, personalViews.selectedOption);
    },
    [personalViews],
  );

  const defaultPersonalViewItem = useMemo(
    () => {
      if ((!personalViews && !personalViews?.options?.length) || isLoading) return null;
      return findDefaultPersonalViewItem(personalViews.options, personalViews.defaultOption);
    },
    [personalViews],
  );

  return {
    // Personal View - Search
    handleSearchPersonalViewsGroups,
    searchPersonalViewsGroups,
    personalViewsListFiltered,

    // Personal View - Selected
    selectedPersonalViewItem,
    defaultPersonalViewItem,

    // State
    popoverPersonalViewIsOpen,
    setPopoverPersonalViewIsOpen,
  };
};

export default useTablePersonalView;
